import { useCenterStore } from '@caradasstore/CenterStore'
import { useLoadingStore } from '@caradasstore/LoadingStore'
import { useMobileToolbarStore } from '@caradasstore/MobileToolbarStore'
import { useUserStore } from '@caradasstore/UserStore'
import { ItemPerPageSelector } from '@components/common'
import { EmailForm, InvoiceEmailVariantModal } from '@components/email'
import { AssigneeAvatarPicker } from '@components/assigned-user'
import { Checkbox } from '@components/ui/checkbox'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@components/ui/table'
import { QueueCard } from '@components/queue-card'
import { QueueSelectionRibbon } from '@components/queue-selection-ribbon'
import { MobileFilterDrawer } from '@components/mobile-filter-drawer'
import { SortableTableHead, nextSort, sortFromQuery, writeSortToQuery } from '@components/table-sort'
import { AdasBox, AdasPagination, AdasPaper, AdasStack } from '@components/wrapper-components'
import { AdasCircularProgress } from '@components/wrapper-components'
import { CheckCheck, Download, Mail, Search, Sparkles, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import {
  closeInvoiceManual,
  getFinanciallyConnected,
  getInvoiceCsvByDateRange,
  getInvoiceEmails,
  getInvoicePdf,
  getInvoicelist,
  sendToQuickBook
} from '../../../api/api'
import {
  BUTTONS_DISPLAY_LABEL,
  DATE_FORMATS,
  GENERIC_MESSAGES,
  getInvoiceSummaryUrl,
  invoiceVariantsForEmailChoice
} from '../../../constants'
import { scrollPageTop, useScrollTop } from '../../../utils/useScrollTop'
import { useIsReadOnly } from '@hooks/useIsReadOnly'
import { formatDate, currencyFormatter, isTech } from '../../../utils/utils'
import { Logger } from '../../../logger'
import { StatusEnum } from 'enums/enums'
import { DatePickerModal } from '@components/common/modal'
import SearchWithSelect from '@components/search-with-select/SearchWithSelect'
import { useSearchFilters } from '@hooks/useSearchFilters'

const formatCreatedAt = (iso) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const date = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
  const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  return `${date} ${time}`
}

// Sortable columns (keys whitelisted by the backend, QwiListSort). The Invoice column sorts by invoice #;
// 'date' is the tab's date column: closed date on the Closed tab, created date otherwise.
const INVOICE_SORT_OPTIONS = [
  { value: 'referenceNumber', label: 'Invoice #' },
  { value: 'client', label: 'Client' },
  { value: 'assignee', label: 'Assignee' },
  { value: 'status', label: 'Status' },
  { value: 'amount', label: 'Amount' },
  { value: 'date', label: 'Invoice date' }
]
const INVOICE_SORT_KEYS = INVOICE_SORT_OPTIONS.map((o) => o.value)

/** sortBy / sort for the invoices API; the default (no column sort) is newest first by the tab's date. */
export const invoiceSortParams = (sort, status) => {
  const dateKey = status === 'CLOSED' ? 'closedAt' : 'createdAt'
  if (!sort) return { sortBy: dateKey, sort: 'DESC' }
  return { sortBy: sort.key === 'date' ? dateKey : sort.key, sort: sort.dir.toUpperCase() }
}

export const InvoiceQueuePage = () => {
  useScrollTop()
  // URL is the source of truth for filter / pagination / search / search-filter
  // chips so that goBack from an invoice summary restores the same view, and a
  // shared link reproduces it.
  const location = useLocation()
  const queryParams = new URLSearchParams(location.search)
  const statusFilter = queryParams.get('status') || 'OPEN'
  const initialPageNo = Math.max(0, Number(queryParams.get('page') || 0))
  const initialPageSize = Math.max(1, Number(queryParams.get('size') || 25))
  const initialSearch = queryParams.get('q') || ''
  const initialPrimarySearchBy = queryParams.get('psb') || 'ro'
  const initialPrimaryQuery = queryParams.get('pq') || ''
  const initialSecondarySearchBy = queryParams.get('ssb') || 'vin'
  const initialSecondaryQuery = queryParams.get('sq') || ''
  const parseJsonParam = (raw) => {
    if (!raw) return {}
    try {
      const parsed = JSON.parse(raw)
      return parsed && typeof parsed === 'object' ? parsed : {}
    } catch {
      return {}
    }
  }
  const initialMultiFilters = parseJsonParam(queryParams.get('mf'))

  const [invoiceList, setInvoiceList] = useState([])
  // Column sort (`?sort=&dir=`); null = newest first by the tab's date (closed on Closed, else created).
  const [sort, setSort] = useState(() => sortFromQuery(queryParams, INVOICE_SORT_KEYS))
  const [selectedInvoices, setSelectedInvoices] = useState({})
  const [pageParams, setPageParams] = useState({
    pageNo: initialPageNo,
    totalPages: 0,
    totalElements: 0,
    search: initialSearch,
    pageSize: initialPageSize,
    status: statusFilter
  })
  const [invoiceClosedTrigger, setInvoiceClosedTrigger] = useState(0)
  const [isEmail, setIsEmail] = useState(false)
  const [isEmailVariantPickerOpen, setIsEmailVariantPickerOpen] = useState(false)
  const [emailContacts, setEmailContacts] = useState({})
  const [invoicePdfs, setInvoicePdfs] = useState([])
  const [emailVariants, setEmailVariants] = useState([])
  const [isConnected, setIsConnected] = useState(false)
  const isReadOnly = useIsReadOnly()
  const [pdfLoader, setPdfLoader] = useState(false)
  const [openDatePickerModal, setOpenDatePickerModal] = useState(false)
  const centerDetail = useCenterStore((store) => store.centerDetail)
  const setLoading = useLoadingStore((store) => store.setLoading)
  const ccId = useCenterStore((store) => store.ccId)
  const user = useUserStore((store) => store.currentUser)

  const {
    primarySearchBy,
    setPrimarySearchBy,
    primaryQuery,
    setPrimaryQuery,
    secondarySearchBy,
    setSecondarySearchBy,
    secondaryQuery,
    setSecondaryQuery,
    multiFilters,
    setMultiFilters,
    searchFilters,
    searchOptions,
    resetSearchFilters
  } = useSearchFilters({
    identifierValue: 'invoice',
    identifierLabel: 'Invoice #',
    initialValues: {
      primarySearchBy: initialPrimarySearchBy,
      primaryQuery: initialPrimaryQuery,
      secondarySearchBy: initialSecondarySearchBy,
      secondaryQuery: initialSecondaryQuery,
      multiFilters: initialMultiFilters
    },
    onFiltersChange: () =>
      setPageParams((prev) => ({
        ...prev,
        pageNo: 0
      }))
  })

  const searchOpen = useMobileToolbarStore((s) => s.searchOpen)
  const filterOpen = useMobileToolbarStore((s) => s.filterOpen)
  const closeMobileToolbar = useMobileToolbarStore((s) => s.closeAll)
  useEffect(() => () => closeMobileToolbar(), [closeMobileToolbar])

  const history = useHistory()
  const redirectToInvoiceSummary = useCallback(
    (data) => {
      history.push(getInvoiceSummaryUrl(data?.reference_number), { status: pageParams.status })
    },
    [history, pageParams.status]
  )

  const selectedIds = Object.keys(selectedInvoices)
  const selectedCount = selectedIds.length
  const isSingleInvoiceSelected = selectedCount === 1
  const canCloseInvoice = (() => {
    if (isTech(user)) return false
    if (selectedCount === 0) return false
    const selectedAndClosed = invoiceList.filter(
      (invoice) => selectedIds.includes(invoice.id + '') && StatusEnum[invoice.status] === StatusEnum.CLOSED
    )
    return selectedAndClosed.length === 0
  })()

  // Switching the status tab resets every active filter (free-text search +
  // search-by chips) so the new tab starts from a clean, unfiltered list.
  const toggleHandler = useCallback(
    (value) => {
      resetSearchFilters()
      setPageParams((prev) => ({
        ...prev,
        status: value,
        pageNo: 0,
        search: ''
      }))
    },
    [resetSearchFilters]
  )

  // Mirror state into the URL so history.goBack from an invoice summary
  // restores the exact list view, and a shared link reproduces it.
  useEffect(() => {
    const params = new URLSearchParams()
    if (pageParams.status) params.set('status', pageParams.status)
    if (pageParams.pageNo > 0) params.set('page', String(pageParams.pageNo))
    if (pageParams.pageSize && pageParams.pageSize !== 25) params.set('size', String(pageParams.pageSize))
    if (pageParams.search) params.set('q', pageParams.search)
    if (primarySearchBy && primarySearchBy !== 'ro') params.set('psb', primarySearchBy)
    if (primaryQuery) params.set('pq', primaryQuery)
    if (secondarySearchBy && secondarySearchBy !== 'vin') params.set('ssb', secondarySearchBy)
    if (secondaryQuery) params.set('sq', secondaryQuery)
    const activeMulti = Object.fromEntries(Object.entries(multiFilters || {}).filter(([, v]) => v != null && v !== ''))
    if (Object.keys(activeMulti).length > 0) params.set('mf', JSON.stringify(activeMulti))
    writeSortToQuery(params, sort)
    const next = params.toString()
    const current = location.search.replace(/^\?/, '')
    if (next !== current) {
      history.replace({ pathname: window.location.pathname, search: next ? `?${next}` : '' })
    }
  }, [
    pageParams.status,
    pageParams.pageNo,
    pageParams.pageSize,
    pageParams.search,
    primarySearchBy,
    primaryQuery,
    secondarySearchBy,
    secondaryQuery,
    multiFilters,
    sort,
    history,
    location.search
  ])

  // A new sort starts from the first page; it survives tab and filter changes.
  const applySort = useCallback((nextValue) => {
    setSort(nextValue)
    setPageParams((prev) => ({ ...prev, pageNo: 0 }))
  }, [])
  const handleSort = useCallback((key) => applySort(nextSort(sort, key)), [applySort, sort])

  const paginationHandler = useCallback((event, page) => {
    setPageParams((prev) => ({ ...prev, pageNo: page - 1 }))
  }, [])

  const pageSizeHandler = useCallback((event) => {
    setPageParams((prev) => ({ ...prev, pageSize: event.target.value, pageNo: 0 }))
  }, [])

  const sendQuickBookHandler = useCallback(async () => {
    setLoading(true)
    const quickbookResponse = await sendToQuickBook({ invoiceIds: selectedIds })
    setLoading(false)
    if (quickbookResponse?.status === 200) {
      setInvoiceClosedTrigger(invoiceClosedTrigger + 1)
      setSelectedInvoices({})
    }
  }, [invoiceClosedTrigger, selectedIds, setLoading])

  useEffect(() => {
    setSelectedInvoices({})
    const fetchInvoiceList = async () => {
      setLoading(true)
      try {
        const response = await getInvoicelist({
          pageNo: pageParams.pageNo,
          pageSize: pageParams.pageSize,
          status: pageParams.status,
          ccid: ccId,
          search: pageParams.search,
          ...invoiceSortParams(sort, pageParams.status),
          searchFilters
        })
        if (response.status === 200) {
          setInvoiceList(response.data.invoices)
          setPageParams((prev) => ({
            ...prev,
            totalPages: response.data.totalPages,
            totalElements: response.data.totalElements ?? response.data.invoices?.length ?? 0
          }))
          scrollPageTop()
        }
      } catch (error) {
        Logger.error({ message: error, payload: { pageParams, ccId } })
      } finally {
        setLoading(false)
      }
    }
    fetchInvoiceList()
  }, [
    ccId,
    pageParams.pageNo,
    pageParams.pageSize,
    pageParams.status,
    pageParams.search,
    searchFilters,
    sort,
    setLoading,
    invoiceClosedTrigger
  ])

  useEffect(() => {
    const getIsFinanciallyConnected = async () => {
      const response = await getFinanciallyConnected({ ccid: centerDetail.id })
      if (response.status === 200) setIsConnected(response.data)
    }
    getIsFinanciallyConnected()
  }, [centerDetail.id])

  // Email Invoice runs in two steps: pick the document(s) to send, then the email form.
  // Nothing is fetched until a choice is made, so cancelling the picker costs no calls.
  const emailInvoiceHandler = useCallback(() => {
    setIsEmailVariantPickerOpen(true)
  }, [])

  // `choice` is an INVOICE_EMAIL_VARIANT_CHOICE: retail, cost, or both. It decides which
  // invoice PDFs are attached and which variants the backend is told to send.
  const startEmailInvoice = useCallback(
    async (choice) => {
      const invoiceId = selectedIds[0]
      const variants = invoiceVariantsForEmailChoice(choice)
      setIsEmailVariantPickerOpen(false)
      setEmailVariants(variants)
      // Drop any attachment left over from a previous choice so the form can't briefly
      // show the wrong document while the new PDFs load.
      setInvoicePdfs([])

      try {
        const emailContactResponse = await getInvoiceEmails({ invoiceId })
        if (emailContactResponse.status === 200) {
          setIsEmail(true)
          setEmailContacts(emailContactResponse.data)
        }
        const responses = await Promise.all(variants.map((variant) => getInvoicePdf({ invoiceId, variant })))
        const files = responses.map((response, index) => {
          if (response?.status !== 200) return null
          const fileValue = new File([response.data], `invoice_${selectedInvoices[invoiceId]}_${variants[index]}.pdf`)
          fileValue.id = invoiceId
          return fileValue
        })
        // All-or-nothing: a partial set would let SEND go out missing a document the
        // user asked for.
        if (files.every(Boolean)) setInvoicePdfs(files)
      } catch (error) {
        Logger.error({ message: error, payload: { InvoiceId: invoiceId, choice, path: 'InvoicePage/startEmailInvoice' } })
      }
    },
    [selectedIds, selectedInvoices]
  )

  const viewPdfHandler = useCallback(async () => {
    for (const invoiceId of selectedIds) {
      try {
        setPdfLoader(true)
        const invoicePdfResponse = await getInvoicePdf({ invoiceId })
        if (invoicePdfResponse.status === 200) {
          const fileValue = new Blob([invoicePdfResponse.data], { type: 'application/pdf' }, `invoice_${selectedInvoices[invoiceId]}.pdf`)
          fileValue.id = invoiceId
          const fileURL = URL.createObjectURL(fileValue)
          const pdfWindow = window.open()
          if (pdfWindow === null) {
            alert('Pop-up was blocked by your browser! Please allow pop-ups for caradasplus.com to view Invoice.')
          } else {
            pdfWindow.location.href = fileURL
          }
        }
      } catch (error) {
        Logger.error({ message: error, payload: { invoiceId, path: 'InvoiceQueuePage/viewPdfHandler' } })
      } finally {
        setPdfLoader(false)
      }
    }
  }, [selectedIds, selectedInvoices])

  const toggleSelect = (item) => {
    setSelectedInvoices((prev) => {
      if (prev[item.id]) {
        // eslint-disable-next-line no-unused-vars
        const { [item.id]: _removed, ...rest } = prev
        return rest
      }
      return { ...prev, [item.id]: item.reference_number }
    })
  }

  const downloadCsvByDate = async (startDate, endDate) => {
    const status = pageParams.status === 'OPEN,CLOSED' ? '' : pageParams.status
    const csvResponse = await getInvoiceCsvByDateRange({ startDate, endDate, ccId, status })
    if (csvResponse.status === 200) {
      const fileName = `caradasplus-invoices-export-${formatDate(startDate, DATE_FORMATS.ISO_DATE_STRING)}-to-${formatDate(endDate, DATE_FORMATS.ISO_DATE_STRING)}.csv`
      const fileValue = new Blob([csvResponse.data], { type: 'application/csv' }, fileName)
      const fileURL = URL.createObjectURL(fileValue)
      const tempLink = document.createElement('a')
      tempLink.href = fileURL
      tempLink.setAttribute('download', fileName)
      tempLink.click()
      setOpenDatePickerModal(false)
    }
  }

  const closeInvoiceHandler = useCallback(async () => {
    const closeInvoicesResponse = await closeInvoiceManual({ invoiceIds: selectedIds })
    if (closeInvoicesResponse.status === 200) {
      setInvoiceClosedTrigger(invoiceClosedTrigger + 1)
      setSelectedInvoices({})
    }
  }, [invoiceClosedTrigger, selectedIds])

  const statusTabs = [
    { value: 'OPEN,CLOSED', label: 'All' },
    { value: 'OPEN', label: 'Open' },
    { value: 'CLOSED', label: 'Closed' }
  ]

  return (
    <div className='mx-auto max-w-[1400px] py-6'>
      <InvoiceEmailVariantModal
        open={isEmailVariantPickerOpen}
        onClose={() => setIsEmailVariantPickerOpen(false)}
        onContinue={startEmailInvoice}
      />
      <EmailForm
        emailType='invoice'
        invoicePdfs={invoicePdfs}
        emailVariants={emailVariants}
        isOpen={isEmail}
        contacts={emailContacts}
        emailData={{ id: selectedIds[0] }}
        onClose={() => setIsEmail(false)}
      />

      {/* Page heading + top-right action cluster. Per CAR-3677 record-level
          actions (View PDF / Email / Close / Send to QBO) now live in the
          selection ribbon; only the page-level "Export CSV" stays here. */}
      <AdasBox className='hidden min-[435px]:flex pt-6 md:pt-4 mb-6 flex-wrap items-center justify-between gap-3'>
        <h1 className='text-[32px] font-bold leading-tight tracking-tight text-foreground'>Invoices</h1>
        <AdasBox className='hidden md:flex items-center gap-3'>
          <button
            disabled={selectedCount > 0}
            onClick={() => setOpenDatePickerModal(true)}
            className='inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-[#D1D5D8] bg-white text-sm font-medium text-[#0A1628] transition-colors hover:bg-[#F8FAFC] disabled:opacity-40 disabled:cursor-not-allowed'
          >
            <Download className='h-4 w-4' />
            Export CSV
          </button>
        </AdasBox>
      </AdasBox>

      {/* Mobile inline search bar */}
      {searchOpen && (
        <AdasBox className='min-[435px]:hidden mb-3 mt-2 flex items-center gap-2 rounded-lg bg-[#F4F6F9] px-3 py-2.5'>
          <Search className='h-4 w-4 text-[#6B7280]' />
          <input
            type='text'
            value={pageParams.search}
            onChange={(e) => setPageParams((prev) => ({ ...prev, search: e.target.value, pageNo: 0 }))}
            autoFocus
            placeholder='Client, VIN, RO, vehicle, invoice #'
            className='flex-1 border-0 bg-transparent text-[14px] text-[#0A1628] placeholder:text-[#6B7280] focus:outline-none'
          />
          {pageParams.search && (
            <button
              onClick={() => setPageParams((prev) => ({ ...prev, search: '', pageNo: 0 }))}
              aria-label='Clear search'
              className='inline-flex h-6 w-6 items-center justify-center rounded-full bg-white text-[#475569]'
            >
              <X className='h-3 w-3' />
            </button>
          )}
        </AdasBox>
      )}

      {/* Status tabs — All / Open / Closed */}
      <AdasBox className='mb-6 flex items-center gap-7 border-b border-[#D1D5D8]'>
        {statusTabs.map(({ value, label }) => {
          const active = pageParams.status === value
          return (
            <button
              key={value}
              type='button'
              onClick={() => toggleHandler(value)}
              className={`pb-3 -mb-px flex items-center gap-2 text-[14px] font-medium transition-colors ${
                active ? 'text-[#1F6FEB] border-b-2 border-[#1F6FEB]' : 'text-[#6B7280] hover:text-[#475569]'
              }`}
            >
              {label}
              {active && pageParams.totalElements > 0 && (
                <span className='inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#DBEAFE] text-[#1F6FEB] text-[11px] font-semibold'>
                  {pageParams.totalElements}
                </span>
              )}
            </button>
          )
        })}
      </AdasBox>

      {/* Desktop filter row */}
      <AdasBox className='mb-5 hidden min-[435px]:flex flex-col md:flex-row items-start md:items-center gap-3'>
        <SearchWithSelect
          selectValue={primarySearchBy}
          onSelectChange={(e) => setPrimarySearchBy(e.target.value)}
          searchValue={primaryQuery}
          onSearchChange={(e) => setPrimaryQuery(e.target.value)}
          options={searchOptions}
          placeholder='Search...'
        />
        <SearchWithSelect
          selectValue={secondarySearchBy}
          onSelectChange={(e) => setSecondarySearchBy(e.target.value)}
          searchValue={secondaryQuery}
          onSearchChange={(e) => setSecondaryQuery(e.target.value)}
          options={searchOptions}
          placeholder='Search...'
        />
      </AdasBox>

      {/* Mobile selection action bar */}
      {selectedCount > 0 && (
        <div
          className='min-[435px]:hidden -mx-3 mb-3 flex items-center justify-between gap-3 px-4 py-3 text-white'
          style={{ background: 'var(--primary-gradient)' }}
        >
          <span className='text-[14px] font-semibold'>{selectedCount} selected</span>
          <div className='flex items-center gap-2'>
            {isSingleInvoiceSelected && (
              <>
                <button
                  onClick={viewPdfHandler}
                  disabled={pdfLoader}
                  className='inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC] disabled:opacity-40'
                >
                  {pdfLoader ? <AdasCircularProgress color='inherit' size={14} /> : <Download className='h-3.5 w-3.5' />}
                  PDF
                </button>
                {!isReadOnly && (
                  <button
                    onClick={emailInvoiceHandler}
                    className='inline-flex items-center gap-1.5 rounded-lg bg-white px-3 py-1.5 text-[13px] font-semibold text-[#0A1628] transition-colors hover:bg-[#F8FAFC]'
                  >
                    <Mail className='h-3.5 w-3.5' />
                    Email
                  </button>
                )}
              </>
            )}
            <button onClick={() => setSelectedInvoices({})} className='text-[13px] font-semibold text-white/80 hover:text-white'>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Mobile card list */}
      <div className='min-[435px]:hidden pt-2 space-y-3'>
        {invoiceList.length === 0 ? (
          <div className='rounded-xl border border-[#E5E7EB] bg-white p-8 text-center text-[13px] text-[#6B7280]'>No invoices found</div>
        ) : (
          invoiceList.map((item) => (
            <QueueCard
              key={item.id}
              item={item}
              itemType='INV'
              isSelected={!!selectedInvoices[item.id]}
              onSelect={toggleSelect}
              onClick={redirectToInvoiceSummary}
              assigneeType='invoices'
            />
          ))
        )}
      </div>

      {/* Desktop selection ribbon — sticky to the top of the scroll area as the
          table scrolls. Rendered as a direct child of the page root (not wrapped)
          so the sticky element has room to travel. No New Appointment (quote-only).
          Close swaps to "Send to QBO" when QuickBooks is connected, and is disabled
          when the selection can't be closed (tech user, or a selected invoice is
          already closed). */}
      <QueueSelectionRibbon
        className='hidden min-[435px]:flex'
        count={selectedCount}
        recordLabelPlural='Invoices'
        closeLabel={isConnected ? 'Send to QBO' : 'Close Invoices'}
        closeDisabled={!canCloseInvoice}
        onViewPdf={viewPdfHandler}
        onEmail={emailInvoiceHandler}
        onClose={isConnected ? sendQuickBookHandler : closeInvoiceHandler}
        onClear={() => setSelectedInvoices({})}
        pdfLoading={pdfLoader}
      />

      {/* Desktop table */}
      <div className='hidden min-[435px]:block overflow-hidden rounded-lg border border-[#D1D5D8] bg-white'>
        <Table>
          <TableHeader>
            <TableRow className='bg-gradient-to-r from-[#2C3D4D] to-[#1F2D3D] hover:from-[#2C3D4D] hover:to-[#1F2D3D]'>
              <TableHead className='w-12 text-white' aria-label='Selection' />
              <SortableTableHead
                label='Invoice'
                sortKey='referenceNumber'
                sort={sort}
                onSort={handleSort}
                className='text-white text-[12px] font-semibold uppercase tracking-wide'
              />
              <SortableTableHead
                label='Client'
                sortKey='client'
                sort={sort}
                onSort={handleSort}
                className='text-white text-[12px] font-semibold uppercase tracking-wide'
              />
              <SortableTableHead
                label='Assignee'
                sortKey='assignee'
                sort={sort}
                onSort={handleSort}
                className='text-white text-[12px] font-semibold uppercase tracking-wide'
              />
              <SortableTableHead
                label='Status'
                sortKey='status'
                sort={sort}
                onSort={handleSort}
                className='text-white text-[12px] font-semibold uppercase tracking-wide'
              />
              <SortableTableHead
                label='Amount'
                sortKey='amount'
                sort={sort}
                onSort={handleSort}
                className='text-white text-[12px] font-semibold uppercase tracking-wide'
              />
              <SortableTableHead
                label={pageParams.status === 'CLOSED' ? 'Invoice Closed' : 'Invoice Created'}
                sortKey='date'
                sort={sort}
                onSort={handleSort}
                className='text-white text-[12px] font-semibold uppercase tracking-wide'
              />
            </TableRow>
          </TableHeader>
          <TableBody>
            {invoiceList.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className='py-10 text-center text-[13px] text-[#6B7280]'>
                  No invoices found
                  {pageParams.totalPages > 1 && ' on this page. Go to next page to search more invoices.'}
                </TableCell>
              </TableRow>
            ) : (
              invoiceList.map((item) => {
                const isOpen = StatusEnum[item.status] === StatusEnum.OPEN
                const isSelected = !!selectedInvoices[item.id]
                const dateValue = pageParams.status === 'CLOSED' ? item.closedAt : item.created_at
                return (
                  <TableRow
                    key={item.id}
                    data-state={isSelected ? 'selected' : undefined}
                    onClick={() => redirectToInvoiceSummary(item)}
                    className='cursor-pointer h-[72px] even:bg-[#F8FAFC] hover:bg-[#F0F4FF] data-[state=selected]:bg-[#E8EFFF] data-[state=selected]:hover:bg-[#E8EFFF]'
                  >
                    <TableCell className='align-middle' onClick={(e) => e.stopPropagation()}>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleSelect(item)}
                        aria-label={`Select invoice ${item.reference_number}`}
                      />
                    </TableCell>
                    <TableCell className='align-middle'>
                      <div className='flex items-start justify-between gap-3'>
                        <div className='flex-1 min-w-0'>
                          <div className='flex flex-wrap items-center gap-x-1.5 text-[12px] text-[#6B7280]'>
                            <span>Invoice #{item.reference_number}</span>
                            {item.repair_order_num && (
                              <>
                                <span className='text-[#CBD5E1]'>|</span>
                                <span>RO #{item.repair_order_num}</span>
                              </>
                            )}
                            {item.type && (
                              <>
                                <span className='text-[#CBD5E1]'>|</span>
                                <span>{item.type === 'SERVICE_ONLY' ? 'Service' : item.type === 'STANDARD' ? 'Standard' : item.type}</span>
                              </>
                            )}
                            {item.insurance_company?.name && (
                              <>
                                <span className='text-[#CBD5E1]'>|</span>
                                <span>{item.insurance_company.name}</span>
                              </>
                            )}
                          </div>
                          <div className='mt-1 flex flex-wrap items-center gap-2'>
                            <span className='text-[15px] font-bold uppercase tracking-tight text-[#0A1628]'>
                              {[item.vehicle?.year, item.vehicle?.make, item.vehicle?.model].filter(Boolean).join(' ') || '—'}
                            </span>
                            {!isOpen && item.quality_review?.is_completed && (
                              <span
                                title='Scorecard completed'
                                aria-label='Scorecard completed'
                                className='inline-flex items-center gap-1 rounded-full bg-[#ECFDF5] px-2 py-0.5 text-[10px] font-bold uppercase text-[#047857]'
                              >
                                <CheckCheck className='h-3 w-3' />
                                Scorecard
                              </span>
                            )}
                          </div>
                          {item.vehicle?.vin && (
                            <div className='mt-0.5 font-mono text-[11px] text-[#6B7280] tracking-wider'>{item.vehicle.vin}</div>
                          )}
                        </div>
                        {item.ai_processed && (
                          <span className='shrink-0 inline-flex h-6 items-center gap-1 rounded-full bg-[#EBEDF0] px-2 text-[10px] font-semibold text-[#475569]'>
                            <Sparkles className='h-3 w-3' />
                            Ai
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className='align-middle text-[13px] text-[#0A1628]'>{item.client?.name || '—'}</TableCell>
                    <TableCell className='align-middle' onClick={(e) => e.stopPropagation()}>
                      <AssigneeAvatarPicker
                        type='invoices'
                        workOrderId={item.id}
                        initialAssignee={item.assigned_to}
                        closedStatus={StatusEnum[item.status] === StatusEnum.CLOSED}
                      />
                    </TableCell>
                    <TableCell className='align-middle'>
                      <span
                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase ${
                          isOpen ? 'bg-[#E8EFFF] text-[#2C3D4D]' : 'bg-[#EBEDF0] text-[#6B7280]'
                        }`}
                      >
                        <span className={`inline-block h-1.5 w-1.5 rounded-full ${isOpen ? 'bg-[#2C3D4D]' : 'bg-[#6B7280]'}`} />
                        {StatusEnum[item.status]}
                      </span>
                    </TableCell>
                    <TableCell className='align-middle text-[15px] font-bold text-[#0A1628]'>
                      {currencyFormatter(item.pre_rebate_total ?? item.total_cost)}
                    </TableCell>
                    <TableCell className='align-middle text-[13px] text-[#6B7280]'>{formatCreatedAt(dateValue)}</TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Mobile filter bottom drawer */}
      <MobileFilterDrawer
        open={filterOpen}
        onClose={closeMobileToolbar}
        primarySearchBy={primarySearchBy}
        setPrimarySearchBy={setPrimarySearchBy}
        primaryQuery={primaryQuery}
        setPrimaryQuery={setPrimaryQuery}
        secondarySearchBy={secondarySearchBy}
        setSecondarySearchBy={setSecondarySearchBy}
        secondaryQuery={secondaryQuery}
        setSecondaryQuery={setSecondaryQuery}
        searchOptions={searchOptions}
        onClear={() => setMultiFilters({})}
        sortOptions={INVOICE_SORT_OPTIONS}
        sort={sort}
        onSortChange={applySort}
      />

      <AdasPaper elevation={0} className='hidden min-[435px]:flex items-center justify-between p-[20px] mt-[20px]'>
        <AdasBox className='w-[20%]'>
          <ItemPerPageSelector itemPerPageList={[25, 50, 100, 200]} pageSizeHandler={pageSizeHandler} pageSize={pageParams.pageSize} />
        </AdasBox>
        <AdasBox>
          <AdasStack spacing={2}>
            <AdasPagination onChange={paginationHandler} page={pageParams.pageNo + 1} count={pageParams.totalPages} shape='rounded' />
          </AdasStack>
        </AdasBox>
      </AdasPaper>

      <DatePickerModal
        dateRangeLimitWarning={GENERIC_MESSAGES.EXPORT_INVOICE_CSV_DATE_RANGE_WARNING}
        title={GENERIC_MESSAGES.EXPORT_INVOICE_CSV}
        message={GENERIC_MESSAGES.INVOICE_EXTRACT}
        open={openDatePickerModal}
        handleClose={() => setOpenDatePickerModal(false)}
        confirmLabel={BUTTONS_DISPLAY_LABEL.CREATE}
        handleSubmit={downloadCsvByDate}
      />
    </div>
  )
}
